import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions"
import { useAgTechGetRequest } from "@agtech/core/data/http/AgTechApiGetRequests"
import { useAgTechHttpPostEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests"
import { SaleOperation, operationDataSlice, DefaultDocumentHeaderConfiguration, DefaultInvoiceDocumentConfiguration, DocumentHeaderConfiguration, InvoiceDocumentConfiguration } from "./SaleDataOperation"
import { ContactCredit } from "./contacts/credits/ContactCreditData"
import { AgTechContact } from "@agtech/data/contacts/ContactData"
import { ContactNote } from "@agtech/data/contacts/notes/ContactNoteData"

/// Operation DTOs

type ContactDataDTO = AgTechContact & {
    notes: ContactNote[],
    credits: ContactCredit[]
}

type SaleOperationDataDTO = SaleOperation & {
    contacts: ContactDataDTO[]
}

/// Operation Actions

export const SaleOperationDataRegisterOperationAction: AgTechDataAction<any, any> = {
    name: 'RegisterOperation',
    getConfiguration: () => ({
        actionExecutionMessage: 'Registering...',
        actionConfirmationMessage: 'Registration successful!'
    }),
    action: async (props) => {
        return await props.http.post({
            request: 'Operation',
            inputData: {}
        });
    }
}

export const SaleOperationDataCompleteOperationSetupAction: AgTechDataAction<any, any> = {
    name: 'CompleteOperationSetup',
    getConfiguration: () => ({
        actionExecutionMessage: 'Completing setup...'
    }),
    action: async (props) => {
        return await props.http.post({
            request: 'Operation/CompleteSetup',
            inputData: {}
        });
    }
}

export const useOperationDataLoadOperationAction = (): AgTechDataAction<any, any> => {
    let operationDataGetRequest = useAgTechGetRequest<SaleOperationDataDTO>();

    return {
        name: 'LoadOperationData',
        action: async () => {
            return await operationDataGetRequest.get({
                path: 'Operation/GetOperationData'
            });
        },
        onSuccess: async props => {
            props.executeReducerAction(operationDataSlice.actions.setOperation, {
                operationId: props.responseData.id ?? 0,
                operationName: props.responseData.operationName ?? '',
                operationHostName: props.responseData.operationHostName ?? '',
                documentHeaderConfiguration: props.responseData.documentHeaderConfiguration ?? DefaultDocumentHeaderConfiguration,
                invoiceDocumentConfiguration: props.responseData.invoiceDocumentConfiguration ?? DefaultInvoiceDocumentConfiguration,
                needsSetup: props.responseData.needsSetup,
                address: props.responseData.address ?? '',
                city: props.responseData.city ?? '',
                stateAbbreviation: props.responseData.stateAbbreviation ?? '',
                zip: props.responseData.zip ?? '',
            });
        }
    }
}

export const useOperationDataUpdateDocumentConfigurationAction = (): AgTechDataAction<DocumentHeaderConfiguration, DocumentHeaderConfiguration> => {
    let updateConfigPostRequest = useAgTechHttpPostEntityRequest<DocumentHeaderConfiguration>();

    return {
        name: 'UpdateDocumentConfiguration',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating document headers...',
            actionConfirmationMessage: `Successfully updated document headers`,
        }),
        action: async props => {
            return await updateConfigPostRequest.post({
                path: "Operation/Settings/DocumentHeaderConfiguration",
                postedData: props.submittedEntity
            });
        },
        onSuccess: async response => {
            response.executeReducerAction(operationDataSlice.actions.updateDocumentHeaderConfiguration, response.responseData);
        }
    }
}

export const useOperationDataUpdateInvoiceDocumentConfigurationAction = (): AgTechDataAction<InvoiceDocumentConfiguration, InvoiceDocumentConfiguration> => {
    let updateConfigPostRequest = useAgTechHttpPostEntityRequest<InvoiceDocumentConfiguration>();

    return {
        name: 'UpdateInvoiceDocumentConfiguration',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating invoice configuration...',
            actionConfirmationMessage: `Successfully updated invoice configuration`,
        }),
        action: async props => {
            return await updateConfigPostRequest.post({
                path: "Operation/Settings/InvoiceDocumentConfiguration",
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async response => {
            response.executeReducerAction(operationDataSlice.actions.updateInvoiceDocumentConfiguration, response.responseData);
        }
    }
}